/**
 * Shared sub-components for PracticeScreen.
 * Used by both mobile and web layouts.
 */

import React from 'react';
import { TouchableOpacity, View } from 'react-native';
import { Box, HStack, Text, VStack } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { colors, FS_XS, FS_SM, FS_MD } from '../../theme';
import { getLessonState } from './usePracticeData';
import type { LessonPathItem, CEFRLevel } from '../../types/lesson';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import { CEFR_LEVELS, translateCEFRLevel } from '../../utils/cefr';

const FS_EMOJI_MD = 20;

/**
 * Every level the contract knows about (ADR-033 D4). Level support ≠ level
 * content: a level with no lessons yet shows the empty state, it is never
 * hidden from the selector.
 *
 * The table itself moved to `utils/cefr` once the vocabulary screen became a
 * second consumer; re-exported here so existing call sites keep working.
 */
export const LEVELS = CEFR_LEVELS;
export { translateCEFRLevel };

/** Translate CEFR level hint using literal keys */
function translateCEFRHint(t: TFunction, level: CEFRLevel): string {
  switch (level) {
    case 'A1': return t('practice.cefrHints.a1');
    case 'A2': return t('practice.cefrHints.a2');
    case 'B1': return t('practice.cefrHints.b1');
    case 'B2': return t('practice.cefrHints.b2');
    case 'C1': return t('practice.cefrHints.c1');
    case 'C2': return t('practice.cefrHints.c2');
  }
}

/** Translate category key using literal keys (required by literal-i18n-keys rule) */
export function translateCategory(t: TFunction, key: string): string {
  switch (key) {
    case 'all': return t('practice.categories.all');
    case 'grammar': return t('practice.categories.grammar');
    case 'vocabulary': return t('practice.categories.vocabulary');
    case 'pronunciation': return t('practice.categories.pronunciation');
    case 'communication': return t('practice.categories.communication');
    case 'spanish-challenges': return t('practice.categories.spanish-challenges');
    case 'listening': return t('practice.categories.listening');
    case 'writing': return t('practice.categories.writing');
    case 'culture': return t('practice.categories.culture');
    default: return key;
  }
}

const CATEGORY_EMOJI: Record<string, string> = {
  grammar: '📝',
  vocabulary: '📚',
  pronunciation: '🗣️',
  communication: '💬',
  'spanish-challenges': '🇪🇸',
  listening: '👂',
  writing: '✍️',
  culture: '🌎',
};

// ─── Level Selector ───────────────────────────────────────────────

export const LevelSelector = React.memo(function LevelSelector({
  selectedLevel,
  onLevelPress,
}: {
  selectedLevel: CEFRLevel;
  onLevelPress: (level: CEFRLevel) => void;
}) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  return (
    <View style={{ marginHorizontal: 20 }}>
      {/* Segmented control */}
      <View
        style={{
          flexDirection: 'row',
          borderRadius: 10,
          backgroundColor: theme.isDark ? '#1E293B' : '#F1F5F9',
          padding: 3,
        }}
      >
        {LEVELS.map((level) => {
          const isActive = selectedLevel === level;
          return (
            <TouchableOpacity
              key={level}
              testID={`practice-level-${level}`}
              accessibilityLabel={`${level} ${translateCEFRLevel(t, level)}`}
              accessibilityRole="button"
              accessibilityState={{ selected: isActive }}
              onPress={() => onLevelPress(level)}
              activeOpacity={0.7}
              style={{
                flex: 1,
                paddingVertical: 8,
                borderRadius: 8,
                backgroundColor: isActive ? colors.primary : 'transparent',
                alignItems: 'center',
              }}
            >
              <Text
                allowFontScaling={false}
                style={{
                  fontSize: FS_SM * fontScale,
                  fontWeight: '600',
                  color: isActive ? '#FFFFFF' : theme.textSecondary,
                  ...ffs,
                }}
              >
                {level}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Description subtitle */}
      <Text
        allowFontScaling={false}
        style={{
          fontSize: FS_XS * fontScale,
          color: theme.textSecondary,
          marginTop: 6,
          textAlign: 'center',
          ...ffs,
        }}
      >
        {translateCEFRLevel(t, selectedLevel)} — {translateCEFRHint(t, selectedLevel)}
      </Text>
    </View>
  );
});

// ─── Continue Lesson Card ──────────────────────────────────────────

export const ContinueLessonCard = React.memo(function ContinueLessonCard({
  slug,
  title,
  category,
  onPress,
}: {
  slug: string;
  title: string;
  category: string;
  onPress: (slug: string, title?: string) => void;
}) {
  const { t } = useTranslation();
  const { fontScale, ffs } = useDisplayTheme();
  const emoji = CATEGORY_EMOJI[category] || '📖';

  return (
    <TouchableOpacity
      testID="practice-continue-lesson"
      accessibilityLabel={`${t('practice.continueLesson')}: ${title}`}
      accessibilityRole="button"
      onPress={() => onPress(slug, title)}
      activeOpacity={0.7}
    >
      <Box
        p="$4"
        rounded="$2xl"
        style={{ backgroundColor: colors.primary }}
      >
        <HStack justifyContent="space-between" alignItems="center">
          <HStack space="sm" alignItems="center" flex={1}>
            <Text allowFontScaling={false} style={{ fontSize: FS_EMOJI_MD * fontScale }}>
              {emoji}
            </Text>
            <VStack flex={1}>
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_XS * fontScale, color: 'rgba(255,255,255,0.8)', ...ffs }}
              >
                {t('practice.continueLesson')}
              </Text>
              <Text
                allowFontScaling={false}
                numberOfLines={1}
                style={{ fontSize: FS_MD * fontScale, fontWeight: '700', color: '#FFFFFF', ...ffs }}
              >
                {title}
              </Text>
            </VStack>
          </HStack>
          <Box px="$3" py="$1" rounded="$full" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: '#FFFFFF', ...ffs }}
            >
              {t('practice.resumeLesson')} →
            </Text>
          </Box>
        </HStack>
      </Box>
    </TouchableOpacity>
  );
});

// ─── Category Section Header ───────────────────────────────────────

export const CategorySectionHeader = React.memo(function CategorySectionHeader({
  section,
  isExpanded,
  onToggle,
}: {
  section: { slug: string; title: string; category: string; data: LessonPathItem[] };
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const { theme, fontScale, ffs } = useDisplayTheme();

  const emoji = CATEGORY_EMOJI[section.category] || '📖';
  const completedCount = section.data.filter((l) => l.completion !== null).length;
  const totalCount = section.data.length;
  const progress = totalCount > 0 ? completedCount / totalCount : 0;

  return (
    <TouchableOpacity
      testID={`practice-section-${section.category}`}
      accessibilityRole="button"
      accessibilityLabel={`${section.title}, ${completedCount} of ${totalCount}`}
      accessibilityState={{ expanded: isExpanded }}
      onPress={onToggle}
      activeOpacity={0.7}
    >
      <Box mx="$5" mt="$2" pb="$2">
        <HStack alignItems="center">
          <Text allowFontScaling={false} style={{ fontSize: FS_EMOJI_MD * fontScale, marginRight: 8 }}>
            {emoji}
          </Text>
          <Text
            allowFontScaling={false}
            numberOfLines={1}
            style={{ fontSize: FS_SM * fontScale, fontWeight: '700', color: theme.text, flex: 1, textTransform: 'uppercase', letterSpacing: 0.5, ...ffs }}
          >
            {section.title}
          </Text>
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, marginRight: 4, ...ffs }}
          >
            {completedCount}/{totalCount}
          </Text>
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary }}
          >
            {isExpanded ? '▲' : '▼'}
          </Text>
        </HStack>
        <View
          style={{
            height: 2,
            borderRadius: 1,
            backgroundColor: theme.isDark ? '#374151' : '#D5DCE5',
            overflow: 'hidden',
            marginTop: 6,
          }}
        >
          <View
            style={{
              height: '100%',
              width: `${Math.min(progress * 100, 100)}%`,
              borderRadius: 1,
              backgroundColor: colors.primary,
            }}
          />
        </View>
      </Box>
    </TouchableOpacity>
  );
});

// ─── Lesson Row ────────────────────────────────────────────────────

export const LessonRow = React.memo(function LessonRow({
  lesson,
  index,
  state,
  onPress,
}: {
  lesson: LessonPathItem;
  index: number;
  state: 'completed' | 'next' | 'available';
  onPress: (slug: string, title?: string) => void;
}) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const isCompleted = state === 'completed';
  const isNext = state === 'next';

  let badgeContent: string;
  let badgeBg: string;
  let badgeColor: string;
  if (isCompleted) {
    badgeContent = '✓';
    badgeBg = colors.primary;
    badgeColor = '#FFFFFF';
  } else {
    badgeContent = String(index + 1);
    badgeBg = theme.isDark ? '#374151' : '#E5E7EB';
    badgeColor = theme.textSecondary;
  }

  return (
    <TouchableOpacity
      testID={`practice-lesson-${lesson.slug}`}
      accessibilityLabel={`${lesson.title}${isCompleted ? `, ${t('common.completed')}` : ''}`}
      accessibilityRole="button"
      onPress={() => onPress(lesson.slug, lesson.title)}
      activeOpacity={0.7}
    >
      <Box
        p="$4"
        rounded="$xl"
        style={{
          backgroundColor: theme.card,
          borderWidth: 1,
          borderColor: theme.border,
        }}
      >
        <HStack space="md" alignItems="center">
          <View
            style={{
              width: 32,
              height: 32,
              borderRadius: 16,
              backgroundColor: badgeBg,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_SM * fontScale, fontWeight: '700', color: badgeColor, ...ffs }}
            >
              {badgeContent}
            </Text>
          </View>

          <VStack flex={1} space="xs">
            <Text
              allowFontScaling={false}
              numberOfLines={2}
              style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.text, ...ffs }}
            >
              {lesson.title}
            </Text>
            <HStack space="sm" alignItems="center">
              {lesson.estimatedMins != null && (
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}
                >
                  {lesson.estimatedMins} min
                </Text>
              )}
              {isNext && (
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: FS_XS * fontScale, color: colors.primary, fontWeight: '600', ...ffs }}
                >
                  {lesson.estimatedMins != null ? '· ' : ''}{t('practice.nextLesson')}
                </Text>
              )}
            </HStack>
          </VStack>
        </HStack>
      </Box>
    </TouchableOpacity>
  );
});

// ─── Footer Tip ────────────────────────────────────────────────────

export const FooterTip = React.memo(function FooterTip() {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  return (
    <Box mx="$5" mt="$4" mb="$2" p="$4" rounded="$xl" style={{ backgroundColor: theme.accentSoft }}>
      <HStack space="sm" alignItems="flex-start">
        <Text allowFontScaling={false} style={{ fontSize: FS_EMOJI_MD * fontScale, ...ffs }}>💡</Text>
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_SM * fontScale, color: '#92400E', fontWeight: '500', flex: 1, ...ffs }}
        >
          {t('practice.tip')}
        </Text>
      </HStack>
    </Box>
  );
});

// Re-export for convenience
export { getLessonState };
